/**
 * History context for sharing selected deposit data and field form state.
 */
import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import type { FieldFormInitialValues } from '@/components/Fields/FieldFormModal';
import type { FieldSummary } from '@/api/fields';

type FieldFormMode = 'create' | 'edit';

interface HistoryContextType {
  selectedDepositId: string | null;
  setSelectedDepositId: React.Dispatch<React.SetStateAction<string | null>>;
  fieldsVersion: number;
  refreshFields: () => void;
  fieldFormOpen: boolean;
  fieldFormMode: FieldFormMode;
  fieldFormInitialValues?: FieldFormInitialValues;
  openCreateFieldForm: () => void;
  openEditFieldForm: (values: FieldFormInitialValues) => void;
  closeFieldForm: () => void;
  onFieldSaved: (field: FieldSummary) => void;
  registerFieldSavedHandler: (handler: ((field: FieldSummary) => void) | null) => void;
}

const HistoryContext = createContext<HistoryContextType | undefined>(undefined);

interface HistoryProviderProps {
  children: ReactNode;
}

export const HistoryProvider: React.FC<HistoryProviderProps> = ({ children }) => {
  const [selectedDepositId, setSelectedDepositId] = useState<string | null>(null);
  const [fieldsVersion, setFieldsVersion] = useState(0);
  const [fieldFormOpen, setFieldFormOpen] = useState(false);
  const [fieldFormMode, setFieldFormMode] = useState<FieldFormMode>('create');
  const [fieldFormInitialValues, setFieldFormInitialValues] = useState<FieldFormInitialValues | undefined>();
  const [fieldSavedHandler, setFieldSavedHandler] = useState<((field: FieldSummary) => void) | null>(null);

  const refreshFields = useCallback(() => setFieldsVersion((v) => v + 1), []);

  const openCreateFieldForm = useCallback(() => {
    setFieldFormMode('create');
    setFieldFormInitialValues(undefined);
    setFieldFormOpen(true);
  }, []);

  const openEditFieldForm = useCallback((values: FieldFormInitialValues) => {
    setFieldFormMode('edit');
    setFieldFormInitialValues(values);
    setFieldFormOpen(true);
  }, []);

  const closeFieldForm = useCallback(() => {
    setFieldFormOpen(false);
    setFieldFormInitialValues(undefined);
  }, []);

  const onFieldSaved = useCallback((field: FieldSummary) => {
    refreshFields();
    if (fieldFormMode === 'create') {
      setSelectedDepositId(field.id);
    }
    fieldSavedHandler?.(field);
    closeFieldForm();
  }, [closeFieldForm, fieldFormMode, fieldSavedHandler, refreshFields]);

  const registerFieldSavedHandler = useCallback((handler: ((field: FieldSummary) => void) | null) => {
    setFieldSavedHandler(handler);
  }, []);

  return (
    <HistoryContext.Provider
      value={{
        selectedDepositId,
        setSelectedDepositId,
        fieldsVersion,
        refreshFields,
        fieldFormOpen,
        fieldFormMode,
        fieldFormInitialValues,
        openCreateFieldForm,
        openEditFieldForm,
        closeFieldForm,
        onFieldSaved,
        registerFieldSavedHandler,
      }}
    >
      {children}
    </HistoryContext.Provider>
  );
};

export const useHistory = () => {
  const context = useContext(HistoryContext);
  if (context === undefined) {
    throw new Error('useHistory must be used within a HistoryProvider');
  }
  return context;
};
